/**
 * PoiCabinet — 興趣點分類加載與展示（舊版圖示／聚合＋行政區 vis）
 */
import { SovereignCabinet } from '../../core/cabinet-base.js';
import { locatePoiDistrict, locatePoiDistrictAt } from '../../utils/poi-district-resolve.js';
import { searchGisPlaces } from '../../utils/gis-place-search.js';
import { resolveImportantBrand, preloadImportantBrandIcons } from '../../utils/poi-important-brands.js';
import { poiKindGlyphInner } from '../../utils/poi-kind-glyphs.js';
import { googleMapsNavButtonHtml } from '../../utils/google-maps-nav.js';

const POI_UI_REV = '17.0.886-poi-viewport-multi';
/** 視圖跨區最多同時載入幾個行政區（防縮太小時打爆 API） */
const MAX_VIEWPORT_DISTRICTS = 8;

const CATEGORY_META = {
    CON: { color: '#00bcd4', size: 22, label: '便利商店' },
    FOD: { color: '#ff9800', size: 22, label: '餐飲美食' },
    SHP: { color: '#9c27b0', size: 22, label: '購物商場' },
    EDU: { color: '#f59e0b', size: 22, label: '教育機構' },
    MED: { color: '#f44336', size: 22, label: '醫療衛生' },
    FIN: { color: '#4caf50', size: 22, label: '金融機構' },
    REC: { color: '#e91e63', size: 22, label: '體育休閒' },
    TRA: { color: '#2196f3', size: 22, label: '交通樞紐' },
    PUB: { color: '#607d8b', size: 22, label: '公共設施' }
};

/** 內嵌 SVG（地圖頁未載入 Font Awesome；舊版 <i class="fas"> 會變成空圓點） */
const POI_GLYPH = {
    CON: '<path d="M4 10h16v9H4z"/><path d="M4 10l2-4h12l2 4"/><path d="M10 14h4v5h-4z"/>',
    FOD: '<path d="M8 4v8"/><path d="M7 4h2"/><path d="M8 12v8"/><path d="M16 4c0 3.2-2.4 4.2-2.4 7V20"/>',
    SHP: '<path d="M7 8h10l-1.1 11H8.1L7 8z"/><path d="M9 8V6.4a3 3 0 0 1 6 0V8"/>',
    EDU: '<path d="M3 11l9-5 9 5-9 5-9-5z"/><path d="M7 13.2v4.2c2.4 1.4 7.6 1.4 10 0v-4.2"/>',
    MED: '<path d="M10 5h4v5h5v4h-5v5h-4v-5H5v-4h5z"/>',
    FIN: '<path d="M4 9l8-5 8 5H4z"/><path d="M6 11v6M12 11v6M18 11v6"/><path d="M4 18h16"/>',
    REC: '<path d="M12 21V11"/><path d="M12 11c-4 0-6 3.2-6 5.2 4 0 6-2.2 6-5.2z"/><path d="M12 11c4 0 6 3.2 6 5.2-4 0-6-2.2-6-5.2z"/><path d="M12 6.5c-2 0-3 2-3 2h6s-1-2-3-2z"/>',
    TRA: '<rect x="6" y="5" width="12" height="11" rx="2"/><circle cx="9" cy="18.2" r="1.4"/><circle cx="15" cy="18.2" r="1.4"/><path d="M9 9h2M13 9h2"/>',
    PUB: '<path d="M4 9l8-5 8 5"/><path d="M6 10v7M12 10v7M18 10v7"/><path d="M4 18h16"/>'
};

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function poiGlyphSvg(category, kind) {
    const kindInner = poiKindGlyphInner(kind);
    const inner = kindInner || POI_GLYPH[category] || '<circle cx="12" cy="12" r="3.5"/>';
    return `<svg class="poi-marker-glyph" viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><g fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${inner}</g></svg>`;
}

/** 色＝大類；線圖＝OSM 小類（h_kind）；無小類圖才退回大類圖 */
function poiIcon(category, kind = null, iconScale = 1) {
    const meta = CATEGORY_META[category] || { color: '#666', size: 22 };
    const size = Math.max(9, Math.round((meta.size || 22) * iconScale));
    const glyph = Math.max(7, Math.round(13 * iconScale));
    const svg = poiGlyphSvg(category, kind).replace(/width="13"/, `width="${glyph}"`).replace(/height="13"/, `height="${glyph}"`);
    return L.divIcon({
        className: 'poi-div-icon',
        html: `<div class="poi-marker poi-marker-${size}" style="background:${meta.color};width:${size}px;height:${size}px">${svg}</div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2]
    });
}

function poiClusterIcon(category, count) {
    const color = (CATEGORY_META[category] || { color: '#607d8b' }).color;
    const size = count < 10 ? 28 : count < 50 ? 32 : 38;
    return L.divIcon({
        className: 'poi-div-icon',
        html: `<div class="poi-cluster" style="background:${color};width:${size}px;height:${size}px">${count}</div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2]
    });
}

/** 九類中文名／柜標籤皆視為「退化標題」，不可當店名 */
const POI_PLACEHOLDER_TITLES = new Set([
    '交通運輸',
    '交通樞紐',
    '餐飲美食',
    '購物百貨',
    '購物商場',
    '便利超商',
    '便利商店',
    '教育學區',
    '教育機構',
    '醫療健康',
    '醫療衛生',
    '休閒公園',
    '體育休閒',
    '金融機構',
    '公共生活',
    '公共設施',
    '設施',
    '未命名設施',
    '其他',
    '活動中心',
    '宗教場所'
]);

function formatPoiSourceLabel(source) {
    const s = String(source || '').trim();
    if (!s || s === 'sovereign') return '主權興趣點庫';
    if (/^cat_[A-Z]{3}\.json$/i.test(s) || /\.geojson$/i.test(s)) return 'OpenStreetMap 興趣點';
    if (/overpass/i.test(s) || /^osm/i.test(s)) return 'OpenStreetMap';
    if (/live-named/i.test(s)) return 'OpenStreetMap（具名點）';
    if (/openstreetmap/i.test(s)) return 'OpenStreetMap';
    return s;
}

function formatPoiUpdatedHkt(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const hkt = new Date(d.getTime() + 8 * 60 * 60 * 1000);
    const y = hkt.getUTCFullYear();
    const m = String(hkt.getUTCMonth() + 1).padStart(2, '0');
    const day = String(hkt.getUTCDate()).padStart(2, '0');
    const hh = String(hkt.getUTCHours()).padStart(2, '0');
    const mm = String(hkt.getUTCMinutes()).padStart(2, '0');
    return `${y}-${m}-${day} ${hh}:${mm} HKT`;
}

/** 鐵路／捷運／輕軌 → 軌道交通；其餘用柜標籤（勿直接秀資料庫「交通運輸」） */
function displayCategoryLabel(p, category, meta) {
    const kind = String(p.h_kind || '');
    const kindName = String(p.h_kind_name || '');
    if (
        /^railway=/.test(kind)
        || /鐵路|捷運|輕軌|軌道/.test(kindName)
        || /捷運|鐵路|輕軌/.test(String(p.h_brand_name || ''))
    ) {
        return '軌道交通';
    }
    return meta.label || p.h_category_name || category;
}

function displayPoiTitle(p, categoryLabel) {
    const raw = String(p.h_name || '').trim();
    if (raw && !POI_PLACEHOLDER_TITLES.has(raw) && raw !== categoryLabel && raw !== p.h_category_name) {
        return raw;
    }
    return p.h_brand_name || p.h_kind_name || raw || '未命名設施';
}

function poiPopupHtml(feature, category, opts = {}) {
    const p = feature.properties || {};
    const meta = CATEGORY_META[category] || { color: '#666', label: category };
    const categoryLabel = displayCategoryLabel(p, category, meta);
    const titleText = displayPoiTitle(p, categoryLabel);
    const name = escapeHtml(titleText);
    const brand = p.h_brand_name && String(p.h_brand_name) !== titleText
        ? `<span class="poi-v-tag">${escapeHtml(p.h_brand_name)}</span>`
        : '';
    const rows = [];
    const navBtn = googleMapsNavButtonHtml(feature);
    if (p.h_addr) {
        rows.push(`<div class="poi-popup-row">📍 ${escapeHtml(p.h_addr)}</div>`);
        if (navBtn) rows.push(navBtn);
    } else if (navBtn) {
        rows.push(navBtn);
    }
    if (p.h_tel) {
        rows.push(
            `<div class="poi-popup-row">📞 <a href="tel:${encodeURIComponent(p.h_tel)}">${escapeHtml(p.h_tel)}</a></div>`
        );
    }
    if (p.h_hours) rows.push(`<div class="poi-popup-row">🕒 ${escapeHtml(p.h_hours)}</div>`);
    if (p.h_website) {
        const href = String(p.h_website).trim();
        if (/^https?:\/\//i.test(href)) {
            rows.push(
                `<div class="poi-popup-row">🌐 <a href="${escapeHtml(href)}" target="_blank" rel="noopener">前往官網</a></div>`
            );
        }
    }
    const kind = String(p.h_kind_name || '').trim();
    const showKind = kind && kind !== categoryLabel;
    const updated = formatPoiUpdatedHkt(p.h_compiled_at || opts.compiledAt || '');
    const sourceLabel = formatPoiSourceLabel(p.source);
    return `
        <div class="poi-popup">
            <div class="poi-popup-header" style="background:${meta.color}">${escapeHtml(categoryLabel)}${showKind ? ` · ${escapeHtml(kind)}` : ''}</div>
            <div class="poi-popup-title">${name}${brand}</div>
            ${rows.join('')}
            <div class="poi-popup-footer">
                <div>資料來源：${escapeHtml(sourceLabel)}</div>
                ${updated ? `<div>最後更新：${escapeHtml(updated)}</div>` : ''}
            </div>
        </div>
    `;
}

function bindPoiFeature(feature, layer, category, opts = {}) {
    layer.bindPopup(poiPopupHtml(feature, category, opts), {
        maxWidth: 320,
        className: 'poi-leaflet-popup'
    });
}

function brandSizeClass(size) {
    if (size >= 40) return 'poi-brand-xl';
    if (size >= 38) return 'poi-brand-lg';
    if (size >= 36) return 'poi-brand-md';
    return 'poi-brand-sm';
}

function brandMapIcon(brand, iconScale = 1) {
    const raw = brand.size || 36;
    const size = Math.max(12, Math.round(raw * iconScale));
    const src = brand.iconUrl;
    return L.divIcon({
        className: 'poi-div-icon',
        html: `<div class="poi-brand-img ${brandSizeClass(size)}" title="${escapeHtml(brand.label)}"><img src="${src}" alt="" width="${size}" height="${size}" decoding="async" draggable="false"/></div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2]
    });
}

function featureMapIcon(feature, category, iconScale = 1) {
    const props = feature?.properties || {};
    const brand = resolveImportantBrand(props);
    if (brand) return brandMapIcon(brand, iconScale);
    return poiIcon(category, props.h_kind || null, iconScale);
}

/**
 * 截圖／縮小時圖示相對街道過大 → 依 zoom 縮小（z16=1，z13≈0.4）
 */
export function poiIconScaleForZoom(zoom) {
    const z = Number(zoom);
    if (!Number.isFinite(z)) return 0.55;
    return Math.min(1, Math.max(0.32, (z - 11.5) / 4.5));
}

/** 知名品牌無條件最前（獨立 pane + 高 zIndex；不進 cluster） */
export const POI_BRAND_Z_OFFSET = 2500;
export const POI_BRAND_PANE = 'poiBrandPane';

function ensurePoiBrandPane(map) {
    if (!map || typeof map.getPane !== 'function') return;
    if (!map.getPane(POI_BRAND_PANE)) {
        map.createPane(POI_BRAND_PANE);
        // markerPane=600；品牌必須壓過一般點與 cluster
        map.getPane(POI_BRAND_PANE).style.zIndex = 650;
        map.getPane(POI_BRAND_PANE).style.pointerEvents = 'auto';
    }
}

function splitBrandFeatures(features) {
    const brands = [];
    const regular = [];
    for (const f of features || []) {
        if (resolveImportantBrand(f?.properties || {})) brands.push(f);
        else regular.push(f);
    }
    return { brands, regular };
}

function featureInBounds(feature, bounds) {
    if (!bounds?.isValid?.()) return true;
    const c = feature?.geometry?.coordinates;
    if (!Array.isArray(c) || c.length < 2) return false;
    const lng = Number(c[0]);
    const lat = Number(c[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    return bounds.contains([lat, lng]);
}

function createPOILayer(category, fc, opts = {}) {
    const pane = opts.pane || null;
    const zIndexOffset = opts.zIndexOffset != null ? opts.zIndexOffset : 0;
    const iconScale = opts.iconScale != null ? Number(opts.iconScale) : 1;
    return L.geoJSON(fc, {
        pointToLayer: (feature, latlng) => {
            const brand = resolveImportantBrand(feature?.properties || {});
            // 禁止 pane:undefined — Leaflet 會覆寫預設 markerPane，隨即 appendChild 炸
            const markerOpts = {
                icon: featureMapIcon(feature, category, iconScale),
                zIndexOffset: brand ? POI_BRAND_Z_OFFSET : zIndexOffset
            };
            if (brand) markerOpts.pane = POI_BRAND_PANE;
            else if (pane) markerOpts.pane = pane;
            return L.marker(latlng, markerOpts);
        },
        onEachFeature: (feature, layer) => bindPoiFeature(feature, layer, category, opts)
    });
}

/** 逐點建 marker（禁止把 GeoJSON LayerGroup 整包塞進 cluster，否則放大不重算散開） */
function buildPoiMarkers(category, features, opts = {}) {
    const pane = opts.pane || null;
    const zIndexOffset = opts.zIndexOffset != null ? opts.zIndexOffset : 0;
    const iconScale = opts.iconScale != null ? Number(opts.iconScale) : 1;
    // 進 cluster 時禁止品牌 pane（否則聚合層抓不到點）
    const allowBrandPane = opts.allowBrandPane !== false;
    const markers = [];
    for (const feature of features || []) {
        const c = feature?.geometry?.coordinates;
        if (!Array.isArray(c) || c.length < 2) continue;
        const lng = Number(c[0]);
        const lat = Number(c[1]);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
        const brand = resolveImportantBrand(feature?.properties || {});
        const useBrandPane = allowBrandPane && brand;
        const markerOpts = {
            icon: featureMapIcon(feature, category, iconScale),
            zIndexOffset: useBrandPane ? POI_BRAND_Z_OFFSET : zIndexOffset
        };
        if (useBrandPane) markerOpts.pane = POI_BRAND_PANE;
        else if (pane) markerOpts.pane = pane;
        const marker = L.marker([lat, lng], markerOpts);
        marker.feature = feature;
        bindPoiFeature(feature, marker, category, opts);
        markers.push(marker);
    }
    return markers;
}

function createPoiClusterGroup(category, onProgress) {
    return L.markerClusterGroup({
        maxClusterRadius: 48,
        disableClusteringAtZoom: 15,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        animate: true,
        chunkedLoading: true,
        chunkInterval: 100,
        chunkDelay: 16,
        chunkProgress: onProgress || null,
        iconCreateFunction: (cluster) => poiClusterIcon(category, cluster.getChildCount())
    });
}

/** addLayers + chunkedLoading：等分批完成再繼續（狀態列才不會假完成） */
function addMarkersChunked(clusterLayer, markers, onProgress) {
    return new Promise((resolve) => {
        if (!markers?.length) {
            resolve();
            return;
        }
        let settled = false;
        const done = () => {
            if (settled) return;
            settled = true;
            resolve();
        };
        const prev = clusterLayer.options.chunkProgress;
        clusterLayer.options.chunkProgress = (processed, total, elapsed) => {
            try {
                if (typeof onProgress === 'function') onProgress(processed, total, elapsed);
                if (typeof prev === 'function') prev(processed, total, elapsed);
            } finally {
                if (processed >= total) done();
            }
        };
        clusterLayer.addLayers(markers);
        // 極少數環境不回呼 chunkProgress
        setTimeout(done, Math.min(120000, 800 + markers.length * 4));
    });
}

window.createPOILayer = createPOILayer;
window.poiIconScaleForZoom = poiIconScaleForZoom;
window.POI_BRAND_Z_OFFSET = POI_BRAND_Z_OFFSET;
window.POI_BRAND_PANE = POI_BRAND_PANE;
window.ensurePoiBrandPane = ensurePoiBrandPane;
window.splitPoiBrandFeatures = splitBrandFeatures;

class PoiCabinet extends SovereignCabinet {
    constructor() {
        super();
        this.layers = {};
        this._brandLayer = null;
        this.districtCtx = null;
        this._viewportTimer = null;
        this._searchTimer = null;
        this._searchHits = [];
        this._searchActive = -1;
        this._refreshGen = 0;
        this._districtGen = 0;
        this._viewportDistricts = [];
        this._viewportDistrictKey = '';
        this._loadingCats = new Map();
        this._catAbortControllers = new Set();
        this._clusterLibPromise = null;
        this.loadAssets('/js/cabinets/poi');
    }

    viewportDistrictsLabel(districts = this._viewportDistricts) {
        const list = districts || [];
        if (!list.length) return this.currentDistrict || '本區';
        if (list.length === 1) return list[0].districtZh;
        if (list.length === 2) {
            return `${list[0].districtZh}＋${list[1].districtZh}`;
        }
        return `${list[0].districtZh}＋${list[1].districtZh}等${list.length}區`;
    }

    prioritizeDistricts(districts, centerCtx) {
        const centerKey = this.districtContextKey(centerCtx);
        const sorted = [...(districts || [])].sort((a, b) => {
            const a0 = this.districtContextKey(a) === centerKey ? 0 : 1;
            const b0 = this.districtContextKey(b) === centerKey ? 0 : 1;
            if (a0 !== b0) return a0 - b0;
            return String(a.districtZh || '').localeCompare(String(b.districtZh || ''), 'zh-Hant');
        });
        return sorted;
    }

    isBrandToggleOn() {
        const el = this.shadowRoot?.getElementById('poiBrandToggle');
        // 預設開啟（無 DOM 時仍置頂）
        return !el || !!el.checked;
    }

    isAllBrandsToggleOn() {
        const el = this.shadowRoot?.getElementById('poiAllBrandsToggle');
        return !!el?.checked;
    }

    abortAllCategoryLoads() {
        for (const ctrl of this._catAbortControllers) {
            try { ctrl.abort(); } catch { /* */ }
        }
        this._catAbortControllers.clear();
        this._loadingCats = new Map();
    }

    onMount() {
        this.ensureGlobalStyles();
        preloadImportantBrandIcons();
        const checkboxes = this.shadowRoot.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach((cb) => {
            cb.addEventListener('change', () => {
                const force = cb.id === 'poiBrandToggle' || cb.id === 'poiAllBrandsToggle';
                this.refreshVisibleLayers({ force }).catch((err) => {
                    console.warn('[POI] refresh', err);
                    this.setStatus(`❌ 更新失敗：${err.message || err}`);
                });
            });
        });
        const searchInput = this.shadowRoot.getElementById('poiSearch');
        if (searchInput) {
            searchInput.addEventListener('input', (e) => this.schedulePoiSearch(e.target.value));
            searchInput.addEventListener('keydown', (e) => this.onPoiSearchKeydown(e));
        }
        this.setStatus(`POI ${POI_UI_REV} · 平移地圖以載入該區`);
        this.loadNationwideCatalog();
    }

    ensureGlobalStyles() {
        const existing = document.querySelector('link[data-poi-map-css]');
        if (existing?.getAttribute('data-poi-map-css') === POI_UI_REV) return;
        if (existing) existing.remove();
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = `/css/poi-map-overlays.css?v=${POI_UI_REV}`;
        link.setAttribute('data-poi-map-css', POI_UI_REV);
        document.head.appendChild(link);
    }

    ensureMarkerClusterLib() {
        if (this._clusterLibsLoaded && typeof L?.markerClusterGroup === 'function') {
            return Promise.resolve();
        }
        if (this._clusterLibPromise) return this._clusterLibPromise;

        this._clusterLibPromise = new Promise((resolve) => {
            if (!document.querySelector('link[data-poi-mc-css="1"]')) {
                for (const href of [
                    '/lib/markercluster/MarkerCluster.css',
                    '/lib/markercluster/MarkerCluster.Default.css'
                ]) {
                    const link = document.createElement('link');
                    link.rel = 'stylesheet';
                    link.href = href;
                    link.setAttribute('data-poi-mc-css', '1');
                    document.head.appendChild(link);
                }
            }
            if (typeof L?.markerClusterGroup === 'function') {
                this._clusterLibsLoaded = true;
                resolve();
                return;
            }
            let script = document.querySelector('script[data-poi-mc-js="1"]');
            if (!script) {
                script = document.createElement('script');
                script.src = '/lib/markercluster/leaflet.markercluster.js';
                script.setAttribute('data-poi-mc-js', '1');
                document.head.appendChild(script);
            }
            const finish = () => {
                this._clusterLibsLoaded = typeof L?.markerClusterGroup === 'function';
                resolve();
            };
            if (typeof L?.markerClusterGroup === 'function') {
                finish();
                return;
            }
            script.addEventListener('load', finish, { once: true });
            script.addEventListener('error', finish, { once: true });
        });
        return this._clusterLibPromise;
    }

    districtContextKey(ctx) {
        return ctx ? `${ctx.countyCode}:${ctx.districtZh}` : '';
    }

    async resolveDistrictContext(districtLabel, countyHint = null) {
        const ctx = await locatePoiDistrict(districtLabel, countyHint);
        if (ctx) return ctx;
        if (this.map) {
            const c = this.map.getCenter();
            return locatePoiDistrictAt(c.lat, c.lng);
        }
        return null;
    }

    scheduleViewportPoiLoad() {
        if (!this.map) return;
        clearTimeout(this._viewportTimer);
        this._viewportTimer = setTimeout(() => this.loadPoiForMapViewport(), 350);
    }

    /** 視圖 bounds（略外擴，避免邊緣閃爍） */
    getViewportLoadBounds() {
        if (!this.map) return null;
        const b = this.map.getBounds();
        if (!b?.isValid?.()) return null;
        try {
            return typeof b.pad === 'function' ? b.pad(0.12) : b;
        } catch {
            return b;
        }
    }

    async loadPoiForMapViewport() {
        if (!this.map) return;
        if (this._ignoreViewportUntil && Date.now() < this._ignoreViewportUntil) return;
        const c = this.map.getCenter();
        let centerCtx = null;
        try {
            centerCtx = await locatePoiDistrictAt(c.lat, c.lng);
        } catch (err) {
            const detail = err?.detail || err?.message || '';
            if (err?.status === 401 || err?.status === 403) {
                this.setStatus('POI 服務拒絕存取（非預期；公開圖層應可匿名載入）');
            } else if (/slug|登錄/i.test(detail)) {
                this.setStatus('行政區登錄表未就緒，無法載入 POI');
            } else if (/界線|districts/i.test(detail)) {
                this.setStatus('行政區界線未就緒，無法載入 POI');
            } else {
                this.setStatus(detail ? `POI 行政區解析失敗：${detail}` : '此位置無法對應行政區 POI');
            }
            return;
        }

        const bounds = this.getViewportLoadBounds();
        let districts = bounds ? await this.listDistrictsIntersectingBounds(bounds) : [];
        if (!districts.length && centerCtx) districts = [centerCtx];
        if (!districts.length) {
            this.setStatus('此位置無法對應行政區 POI');
            return;
        }

        districts = this.prioritizeDistricts(districts, centerCtx);
        const found = districts.length;
        const truncated = found > MAX_VIEWPORT_DISTRICTS;
        if (truncated) districts = districts.slice(0, MAX_VIEWPORT_DISTRICTS);

        const key = districts.map((d) => this.districtContextKey(d)).join('|');
        if (key === this._viewportDistrictKey && this.distIndex) {
            // 同組行政區：已在圖上，勿每次平移重砌
            return;
        }

        const primary = centerCtx
            && districts.some((d) => this.districtContextKey(d) === this.districtContextKey(centerCtx))
            ? centerCtx
            : districts[0];
        window.lastSelectedDistrict = primary.districtZh;
        this.setStatus(
            truncated
                ? `正在索引視圖 ${found} 區（載入最近 ${MAX_VIEWPORT_DISTRICTS} 區）…`
                : `正在索引視圖 ${districts.length} 個行政區…`
        );
        await this.loadDistrictsPoi(districts, primary);
    }

    async checkAndReload() {
        if (!this.map) return;
        this._viewportDistrictKey = '';
        await this.loadPoiForMapViewport();
    }

    onMapReady(map) {
        this.map = map;
        this.ensureGlobalStyles();
        ensurePoiBrandPane(map);
        this._onMoveEnd = () => this.scheduleViewportPoiLoad();
        this._onZoomEnd = () => this.refreshAllClusters();
        map.on('moveend', this._onMoveEnd);
        map.on('zoomend', this._onZoomEnd);
        this.ensureMarkerClusterLib().finally(() => this.scheduleViewportPoiLoad());
    }

    /** 放大後強制重算聚合（修「數字不散開」） */
    refreshAllClusters() {
        Object.values(this.layers || {}).forEach((layer) => {
            if (layer && typeof layer.refreshClusters === 'function') {
                try {
                    layer.refreshClusters();
                } catch {
                    /* ignore */
                }
            }
        });
    }

    onUnmount() {
        this._districtGen += 1;
        this._refreshGen += 1;
        this.abortAllCategoryLoads();
        this.clearAllPoiLayers();
        if (this._viewportTimer) clearTimeout(this._viewportTimer);
        if (this._searchTimer) clearTimeout(this._searchTimer);
        if (this.map && this._onMoveEnd) this.map.off('moveend', this._onMoveEnd);
        if (this.map && this._onZoomEnd) this.map.off('zoomend', this._onZoomEnd);
        window.poiLayers = {};
        window.poiBrandLayer = null;
        this.setStatus('已撤除');
    }

    setStatus(text) {
        const el = this.shadowRoot.getElementById('status-text');
        if (el) el.innerText = text;
    }

    async loadNationwideCatalog() {
        try {
            const res = await fetch('/api/poi/catalog', { credentials: 'include' });
            if (!res.ok) return;
            this.catalog = await res.json();
            const n = Number(this.catalog?.metadata?.total_count || 0);
            const d = Number(this.catalog?.metadata?.district_count || 0);
            if (!this.districtCtx) {
                this.setStatus(`全國索引 ${d} 區／${n.toLocaleString()} 點 · 平移地圖載入該區`);
            }
        } catch {
            /* 未登入或目錄尚未建置時維持預設狀態 */
        }
    }

    updateCategoryCounts(counts = {}) {
        this.shadowRoot.querySelectorAll('[data-cat-count]').forEach((el) => {
            const cat = el.getAttribute('data-cat-count');
            const n = Number(counts[cat] || 0);
            el.textContent = n ? String(n) : '';
        });
    }

    async loadDistrictPoi(ctxOrLabel) {
        if (!this.map) return;
        const ctx =
            typeof ctxOrLabel === 'object' && ctxOrLabel?.countyCode
                ? ctxOrLabel
                : await this.resolveDistrictContext(String(ctxOrLabel || ''));
        if (!ctx) {
            this.setStatus('❌ 無法解析行政區');
            return;
        }
        return this.loadDistrictsPoi([ctx], ctx);
    }

    /**
     * 載入一個或多個行政區索引，合併分類計數；分類點由 ensureCategoryLoaded 跨區合併。
     */
    async loadDistrictsPoi(districts, primaryCtx) {
        if (!this.map || !districts?.length) return;
        const primary = primaryCtx || districts[0];
        const dgen = ++this._districtGen;
        this.abortAllCategoryLoads();
        this._refreshGen += 1;

        const label = this.viewportDistrictsLabel(districts);
        this.setStatus(`正在索引 ${label}…`);

        try {
            const settled = await Promise.all(districts.map(async (d) => {
                try {
                    const res = await fetch(
                        `/api/poi/${encodeURIComponent(d.countyCode)}/${encodeURIComponent(d.districtZh)}`,
                        { credentials: 'include' }
                    );
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    const indexData = await res.json();
                    if (!indexData.metadata) throw new Error('索引載入失敗');
                    return { d, indexData };
                } catch (err) {
                    console.warn(`[POI] index ${d.districtZh}:`, err?.message || err);
                    return null;
                }
            }));
            if (dgen !== this._districtGen) return;
            const indexes = settled.filter(Boolean);
            if (!indexes.length) throw new Error('無可用行政區索引');

            const counts = {};
            let total = 0;
            let compiledAt = null;
            for (const { indexData } of indexes) {
                total += Number(indexData.metadata.total_count || 0);
                if (!compiledAt && indexData.metadata?.compiled_at) {
                    compiledAt = indexData.metadata.compiled_at;
                }
                for (const [k, v] of Object.entries(indexData.metadata.categories || {})) {
                    counts[k] = (counts[k] || 0) + Number(v || 0);
                }
            }

            this._viewportDistricts = indexes.map((x) => x.d);
            this._viewportDistrictKey = this._viewportDistricts.map((d) => this.districtContextKey(d)).join('|');
            const primaryOk = indexes.find(
                (x) => this.districtContextKey(x.d) === this.districtContextKey(primary)
            );
            this.districtCtx = primaryOk?.d || indexes[0].d;
            this.currentDistrict = this.viewportDistrictsLabel(this._viewportDistricts);
            this.distIndex = primaryOk?.indexData || indexes[0].indexData;
            this.districtCompiledAt = compiledAt || this.distIndex.metadata?.compiled_at || null;
            this.categoryData = {};
            this.updateCategoryCounts(counts);

            this.clearAllPoiLayers();

            await this.refreshVisibleLayers({ force: true });
            if (dgen !== this._districtGen) return;
            if (!Object.keys(this.layers).length && !this._brandLayer) {
                this.setStatus(
                    `✅ ${this.currentDistrict} 共 ${total} 點 · 勾選分類以顯示 · ${POI_UI_REV}`
                );
            }
        } catch (err) {
            if (dgen !== this._districtGen) return;
            console.error('[POI] Load err:', err);
            this.setStatus(`❌ ${label} 無 POI 資料或 API 失敗`);
        }
    }

    async ensureCategoryLoaded(category, gen = null, dgen = null) {
        if (this.categoryData[category]) return;
        if (!this.districtCtx && !this._viewportDistricts?.length) return;
        if (dgen != null && dgen !== this._districtGen) return;

        if (this._loadingCats?.has(category)) {
            await this._loadingCats.get(category);
            if (this.categoryData[category]) return;
            if (gen != null && gen !== this._refreshGen) return;
            if (dgen != null && dgen !== this._districtGen) return;
        }

        if (gen != null && gen !== this._refreshGen) return;
        if (dgen != null && dgen !== this._districtGen) return;

        const districts = (this._viewportDistricts?.length
            ? this._viewportDistricts
            : [this.districtCtx]).filter(Boolean);
        if (!districts.length) return;

        const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
        if (ctrl) this._catAbortControllers.add(ctrl);
        const timer = ctrl ? setTimeout(() => ctrl.abort(), 60000) : null;
        const onSuperseded = () => {
            if (
                (gen != null && gen !== this._refreshGen)
                || (dgen != null && dgen !== this._districtGen)
            ) {
                try { ctrl?.abort(); } catch { /* */ }
            }
        };
        const poll = setInterval(onSuperseded, 200);

        const job = (async () => {
            try {
                const seen = new Set();
                const merged = [];
                await Promise.all(districts.map(async (d) => {
                    try {
                        const res = await fetch(
                            `/api/poi/${encodeURIComponent(d.countyCode)}/${encodeURIComponent(d.districtZh)}/${category}?v=${encodeURIComponent(POI_UI_REV)}`,
                            {
                                credentials: 'include',
                                cache: 'default',
                                signal: ctrl?.signal
                            }
                        );
                        if (!res.ok) {
                            console.warn(`[POI] ${d.districtZh}/${category} HTTP ${res.status}`);
                            return;
                        }
                        const data = await res.json();
                        const compiledAt = data.metadata?.compiled_at || this.districtCompiledAt || null;
                        for (const f of data.features || []) {
                            const c = f?.geometry?.coordinates;
                            const lng = Array.isArray(c) ? Number(c[0]) : NaN;
                            const lat = Array.isArray(c) ? Number(c[1]) : NaN;
                            const id = f.properties?.h_uid
                                || f.properties?.osm_id
                                || (Number.isFinite(lng) && Number.isFinite(lat)
                                    ? `${lng.toFixed(6)},${lat.toFixed(6)}`
                                    : null);
                            if (!id) continue;
                            const key = `${category}:${id}`;
                            if (seen.has(key)) continue;
                            seen.add(key);
                            if (!compiledAt) {
                                merged.push(f);
                                continue;
                            }
                            const props = {
                                ...(f.properties || {}),
                                h_compiled_at: f.properties?.h_compiled_at || compiledAt
                            };
                            merged.push({ ...f, properties: props });
                        }
                    } catch (err) {
                        if (err?.name === 'AbortError') throw err;
                        console.warn(`[POI] ${d.districtZh}/${category}:`, err?.message || err);
                    }
                }));
                if (gen != null && gen !== this._refreshGen) return;
                if (dgen != null && dgen !== this._districtGen) return;
                this.categoryData[category] = merged;
            } catch (e) {
                if (gen != null && gen !== this._refreshGen) return;
                if (dgen != null && dgen !== this._districtGen) return;
                if (e?.name === 'AbortError') {
                    console.warn(`[POI] ${category} 加載逾時／取消`);
                    return;
                }
                console.warn(`[POI-Atomic] Category ${category} load failed:`, e);
                this.categoryData[category] = [];
            } finally {
                if (timer) clearTimeout(timer);
                clearInterval(poll);
                if (ctrl) this._catAbortControllers.delete(ctrl);
                this._loadingCats?.delete(category);
            }
        })();

        this._loadingCats.set(category, job);
        await job;
    }

    clearAllPoiLayers() {
        Object.keys(this.layers).forEach((cat) => {
            try { this.map.removeLayer(this.layers[cat]); } catch { /* */ }
            delete this.layers[cat];
        });
        if (this._brandLayer) {
            try { this.map.removeLayer(this._brandLayer); } catch { /* */ }
            this._brandLayer = null;
        }
        this.syncLegacyPoiLayers();
    }

    async refreshVisibleLayers(opts = {}) {
        if (!this.map) return;
        if (!this._viewportDistricts?.length || !this.distIndex) {
            await this.loadPoiForMapViewport();
            return;
        }
        const gen = ++this._refreshGen;
        const dgen = this._districtGen;
        const brandOn = this.isBrandToggleOn();
        const allBrandsOn = this.isAllBrandsToggleOn();

        try {
            await this.ensureMarkerClusterLib();
            if (gen !== this._refreshGen || dgen !== this._districtGen) return;
            ensurePoiBrandPane(this.map);

            const checkboxes = this.shadowRoot.querySelectorAll(
                'input[type="checkbox"][value]'
            );
            const activeCategories = Array.from(checkboxes)
                .filter((cb) => cb.checked && cb.id !== 'poiBrandToggle' && cb.id !== 'poiAllBrandsToggle')
                .map((cb) => String(cb.value || '').toUpperCase())
                .filter(Boolean);

            Object.keys(this.layers).forEach((cat) => {
                if (!activeCategories.includes(cat)) {
                    try { this.map.removeLayer(this.layers[cat]); } catch { /* */ }
                    delete this.layers[cat];
                }
            });
            if (!activeCategories.length && !allBrandsOn) {
                this.clearAllPoiLayers();
                this.setStatus(
                    `✅ ${this.currentDistrict || '本區'} 已關閉全部 POI · 勾選分類或品牌以顯示`
                );
                return;
            }
            this.syncLegacyPoiLayers();

            const allMetaCats = Object.keys(CATEGORY_META);
            const requiredCats = allBrandsOn
                ? Array.from(new Set([...activeCategories, ...allMetaCats]))
                : activeCategories;
            const toLoad = requiredCats.filter((cat) => !this.categoryData[cat]);
            let loadFailed = [];
            if (toLoad.length) {
                this.setStatus(
                    `📥 加載 [${toLoad.map((c) => CATEGORY_META[c]?.label || c).join('、')}]…`
                );
                await Promise.all(toLoad.map((cat) => this.ensureCategoryLoaded(cat, gen, dgen)));
                if (gen !== this._refreshGen || dgen !== this._districtGen) return;
                loadFailed = toLoad.filter((cat) => !this.categoryData[cat]);
            }

            for (const cat of activeCategories) {
                if (gen !== this._refreshGen || dgen !== this._districtGen) return;
                const stillOn = this.shadowRoot.querySelector(
                    `input[type="checkbox"][value="${cat}"]`
                )?.checked;
                if (!stillOn) {
                    if (this.layers[cat]) {
                        try { this.map.removeLayer(this.layers[cat]); } catch { /* */ }
                        delete this.layers[cat];
                    }
                    continue;
                }
                if (this.layers[cat] && !toLoad.includes(cat) && !opts.force) {
                    continue;
                }
                if (this.layers[cat]) {
                    try { this.map.removeLayer(this.layers[cat]); } catch { /* */ }
                    delete this.layers[cat];
                }
                const raw = this.categoryData[cat] || [];
                // 品牌置頂開：一般點進 cluster；關：全部（含品牌）進 cluster
                const clusterFeats = brandOn
                    ? splitBrandFeatures(raw).regular
                    : raw;

                if (!clusterFeats.length) continue;

                this.setStatus(`📥 繪製 [${CATEGORY_META[cat]?.label || cat}] ${clusterFeats.length} 點…`);
                await new Promise((r) => setTimeout(r, 0));
                if (gen !== this._refreshGen || dgen !== this._districtGen) return;

                const markers = buildPoiMarkers(cat, clusterFeats, {
                    compiledAt: this.districtCompiledAt,
                    allowBrandPane: false
                });
                let layer;
                if (typeof L.markerClusterGroup === 'function') {
                    layer = createPoiClusterGroup(cat);
                    layer.addTo(this.map);
                    await addMarkersChunked(layer, markers, (processed, total) => {
                        if (gen !== this._refreshGen) return;
                        if (processed === total || processed % 200 === 0) {
                            this.setStatus(
                                `📥 繪製 [${CATEGORY_META[cat]?.label || cat}] ${processed}/${total}`
                            );
                        }
                    });
                } else {
                    layer = L.layerGroup(markers).addTo(this.map);
                }
                if (gen !== this._refreshGen || dgen !== this._districtGen) {
                    try { this.map.removeLayer(layer); } catch { /* */ }
                    return;
                }
                this.layers[cat] = layer;
            }

            if (gen !== this._refreshGen || dgen !== this._districtGen) return;

            if (this._brandLayer) {
                try { this.map.removeLayer(this._brandLayer); } catch { /* */ }
                this._brandLayer = null;
            }

            if (brandOn || allBrandsOn) {
                const brandFeats = [];
                const brandSourceCats = allBrandsOn ? allMetaCats : activeCategories;
                for (const cat of brandSourceCats) {
                    if (!allBrandsOn) {
                        const stillOn = this.shadowRoot.querySelector(
                            `input[type="checkbox"][value="${cat}"]`
                        )?.checked;
                        if (!stillOn) continue;
                    }
                    const { brands } = splitBrandFeatures(this.categoryData[cat] || []);
                    brandFeats.push(...brands.map((f) => ({ cat, f })));
                }
                if (brandFeats.length) {
                    const byCat = new Map();
                    for (const { cat, f } of brandFeats) {
                        if (!byCat.has(cat)) byCat.set(cat, []);
                        byCat.get(cat).push(f);
                    }
                    const group = L.layerGroup();
                    for (const [cat, feats] of byCat) {
                        const layer = createPOILayer(cat, { type: 'FeatureCollection', features: feats }, {
                            compiledAt: this.districtCompiledAt,
                            pane: POI_BRAND_PANE,
                            zIndexOffset: POI_BRAND_Z_OFFSET
                        });
                        group.addLayer(layer);
                    }
                    group.addTo(this.map);
                    this._brandLayer = group;
                    if (typeof group.eachLayer === 'function') {
                        group.eachLayer((ly) => {
                            if (typeof ly.bringToFront === 'function') ly.bringToFront();
                            if (typeof ly.eachLayer === 'function') {
                                ly.eachLayer((m) => {
                                    if (typeof m.setZIndexOffset === 'function') {
                                        m.setZIndexOffset(POI_BRAND_Z_OFFSET);
                                    }
                                    if (typeof m.bringToFront === 'function') m.bringToFront();
                                });
                            }
                        });
                    }
                }
            }

            this.syncLegacyPoiLayers();
            const shown = activeCategories.reduce((acc, cat) => acc + (this.categoryData[cat]?.length || 0), 0);
            const brandCount = (brandOn || allBrandsOn) ? (this._brandLayer ? (this._brandLayer.getLayers ? this._brandLayer.getLayers().reduce((acc, l) => acc + (l.getLayers ? l.getLayers().length : 1), 0) : 0) : 0) : 0;
            if (loadFailed.length && !shown && !brandCount) {
                this.setStatus(
                    `❌ 加載失敗／逾時：${loadFailed.map((c) => CATEGORY_META[c]?.label || c).join('、')}（可再勾選重試）`
                );
                return;
            }
            let msg = `✅ ${this.currentDistrict || ''}`;
            if (activeCategories.length) {
                msg += ` ${shown} 點`;
            }
            if (allBrandsOn && brandCount > 0) {
                msg += `${activeCategories.length ? ' · ' : ' '}全品牌 ${brandCount} 點`;
            } else if (brandOn && this._brandLayer) {
                msg += ' · 品牌置頂';
            } else if (!brandOn) {
                msg += ' · 品牌併入聚合';
            }
            if (loadFailed.length) {
                msg += ` · 部分失敗：${loadFailed.map((c) => CATEGORY_META[c]?.label || c).join('、')}`;
            }
            this.setStatus(msg);
        } catch (err) {
            if (gen === this._refreshGen) {
                console.warn('[POI] refreshVisibleLayers', err);
                this.setStatus(`❌ 更新失敗：${err.message || err}`);
            }
        }
    }

    /**
     * 截圖專用：框內相交行政區全部載入（活地圖只載一區會缺半張圖）
     * 結果寫入 this._screenshotPool = { CAT: Feature[] }
     */
    async prepareScreenshotPool(bounds, categories = null) {
        const cats = categories?.length
            ? categories
            : Array.from(this.shadowRoot?.querySelectorAll('input[type="checkbox"]:checked') || [])
                .map((cb) => String(cb.value || '').toUpperCase())
                .filter(Boolean);
        if (!cats.length || !bounds?.isValid?.()) {
            this._screenshotPool = null;
            return { districts: 0, points: 0, categories: [] };
        }

        this.setStatus('📥 截圖：載入框內各區 POI…');
        let districts = await this.listDistrictsIntersectingBounds(bounds);
        if (!districts.length && this.districtCtx) {
            districts = [{
                countyCode: this.districtCtx.countyCode,
                districtZh: this.districtCtx.districtZh
            }];
        }

        const pool = {};
        for (const cat of cats) pool[cat] = [];
        const seen = new Set();

        await Promise.all(districts.map(async (d) => {
            await Promise.all(cats.map(async (cat) => {
                try {
                    const res = await fetch(
                        `/api/poi/${encodeURIComponent(d.countyCode)}/${encodeURIComponent(d.districtZh)}/${cat}?v=${encodeURIComponent(POI_UI_REV)}`,
                        { credentials: 'include', cache: 'no-store' }
                    );
                    if (!res.ok) return;
                    const data = await res.json();
                    for (const f of data.features || []) {
                        const c = f?.geometry?.coordinates;
                        if (!Array.isArray(c) || c.length < 2) continue;
                        const lng = Number(c[0]);
                        const lat = Number(c[1]);
                        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
                        if (!bounds.contains([lat, lng])) continue;
                        const id = f.properties?.h_uid
                            || f.properties?.osm_id
                            || `${cat}:${lng.toFixed(6)},${lat.toFixed(6)}`;
                        const key = `${cat}:${id}`;
                        if (seen.has(key)) continue;
                        seen.add(key);
                        pool[cat].push(f);
                    }
                } catch (err) {
                    console.warn(`[POI] screenshot pool ${d.districtZh}/${cat}:`, err.message);
                }
            }));
        }));

        this._screenshotPool = pool;
        const points = cats.reduce((n, c) => n + (pool[c]?.length || 0), 0);
        this.setStatus(`📥 截圖池：${districts.length} 區 · ${points} 點`);
        return { districts: districts.length, points, categories: cats };
    }

    async listDistrictsIntersectingBounds(bounds) {
        if (!bounds?.isValid?.()) return [];
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        const q = new URLSearchParams({
            xmin: String(sw.lng),
            ymin: String(sw.lat),
            xmax: String(ne.lng),
            ymax: String(ne.lat)
        });
        try {
            const res = await fetch(`/api/live/boundaries/districts?${q}`, {
                credentials: 'include',
                headers: { Accept: 'application/json' }
            });
            if (!res.ok) return [];
            const fc = await res.json();
            const candidates = [];
            const seenName = new Set();
            for (const f of fc.features || []) {
                const districtZh = f.properties?.name || f.properties?.TOWNNAME;
                const countyZh = f.properties?.county_zh || f.properties?.COUNTYNAME;
                if (!districtZh || !countyZh) continue;
                const nk = `${countyZh}:${districtZh}`;
                if (seenName.has(nk)) continue;
                seenName.add(nk);
                candidates.push({ districtZh, countyZh });
            }
            // MOI county_code 是數字，POI 路徑要主權字母碼 → 一律走 locate
            const out = [];
            const seen = new Set();
            await Promise.all(candidates.map(async ({ districtZh, countyZh }) => {
                try {
                    const loc = await fetch(
                        `/api/poi/locate?q=${encodeURIComponent(districtZh)}&county=${encodeURIComponent(countyZh)}`,
                        { credentials: 'include' }
                    );
                    if (!loc.ok) return;
                    const hit = await loc.json();
                    if (!hit?.countyCode || !hit?.districtZh) return;
                    const key = `${hit.countyCode}:${hit.districtZh}`;
                    if (seen.has(key)) return;
                    seen.add(key);
                    out.push({
                        countyCode: hit.countyCode,
                        districtZh: hit.districtZh,
                        countyZh: hit.countyZh || countyZh
                    });
                } catch {
                    /* ignore */
                }
            }));
            return out;
        } catch {
            return [];
        }
    }

    async showAllCategories() {
        const checkboxes = this.shadowRoot.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach((cb) => {
            cb.checked = true;
        });
        return this.refreshVisibleLayers({ force: true });
    }

    syncLegacyPoiLayers() {
        window.poiLayers = { ...this.layers };
        window.poiBrandLayer = this._brandLayer || null;
    }

    filterPoi(query) {
        this.schedulePoiSearch(query);
    }

    schedulePoiSearch(raw) {
        clearTimeout(this._searchTimer);
        this._searchTimer = setTimeout(() => {
            this.runPoiSearch(raw).catch((err) => console.warn('[POI] search', err));
        }, 140);
    }

    renderPoiSearchHits(hits) {
        const box = this.shadowRoot?.getElementById('poiSearchHits');
        if (!box) return;
        const q = String(this.shadowRoot.getElementById('poiSearch')?.value || '').trim();
        if (!q) {
            box.hidden = true;
            box.innerHTML = '';
            return;
        }
        if (!hits?.length) {
            box.hidden = false;
            box.innerHTML = '<div class="gis-search-empty">沒有符合的店家</div>';
            return;
        }
        box.hidden = false;
        box.innerHTML = hits
            .map((hit, i) => {
                const active = i === this._searchActive ? ' is-active' : '';
                return `<button type="button" class="gis-search-hit${active}" data-search-i="${i}">
                    <b>${escapeHtml(hit.name)}</b>
                    <div style="font-size:11px;color:#94a3b8">${escapeHtml(hit.sub || '')}</div>
                </button>`;
            })
            .join('');
        box.querySelectorAll('.gis-search-hit').forEach((btn) => {
            btn.addEventListener('click', () => {
                const i = Number(btn.getAttribute('data-search-i'));
                const hit = this._searchHits[i];
                if (hit) this.selectPoiSearchHit(hit);
            });
        });
    }

    async runPoiSearch(raw) {
        const q = String(raw || '').trim();
        if (!q) {
            this._searchHits = [];
            this._searchActive = -1;
            this.renderPoiSearchHits([]);
            return;
        }
        const { hits } = await searchGisPlaces(q, {
            kinds: 'poi',
            limit: 16,
            city: this.districtCtx?.countyZh || null,
            district: this.districtCtx?.districtZh || null
        });
        this._searchHits = hits || [];
        this._searchActive = this._searchHits.length ? 0 : -1;
        this.renderPoiSearchHits(this._searchHits);
        this.setStatus(
            this._searchHits.length ? `🔍 ${this._searchHits.length} 筆店家` : `🔍 沒有「${q}」`
        );
    }

    onPoiSearchKeydown(e) {
        const hits = this._searchHits;
        if (e.key === 'Escape') {
            this.renderPoiSearchHits([]);
            this.shadowRoot.getElementById('poiSearch')?.blur();
            return;
        }
        if (!hits.length) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this._searchActive = (this._searchActive + 1) % hits.length;
            this.renderPoiSearchHits(hits);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this._searchActive = (this._searchActive - 1 + hits.length) % hits.length;
            this.renderPoiSearchHits(hits);
        } else if (e.key === 'Enter' && this._searchActive >= 0) {
            e.preventDefault();
            this.selectPoiSearchHit(hits[this._searchActive]);
        }
    }

    async selectPoiSearchHit(hit) {
        if (!hit || !this.map) return;
        const cat = String(hit.cat || '').toUpperCase();
        const cb = this.shadowRoot.querySelector(`input[type="checkbox"][value="${cat}"]`);
        if (cb && !cb.checked) cb.checked = true;

        const box = this.shadowRoot.getElementById('poiSearchHits');
        if (box) box.hidden = true;

        const lat = Number(hit.lat);
        const lon = Number(hit.lon);
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
            this.map.setView([lat, lon], 17);
            // 依照視圖邊界相交的多行政區共同邏輯載入
            await this.loadPoiForMapViewport();
            setTimeout(() => this.openPoiPopupAt(cat, lon, lat, hit.name), 350);
        } else {
            await this.refreshVisibleLayers();
        }
        this.setStatus(`已定位 ${hit.name || ''}`);
    }

    openPoiPopupAt(category, lon, lat, name) {
        const roots = [this.layers[category], this._brandLayer].filter(Boolean);
        if (!roots.length) return;
        const matchName = String(name || '').trim();
        let found = null;
        const walk = (lyr) => {
            if (lyr.eachLayer && !lyr.getLatLng) {
                lyr.eachLayer(walk);
                return;
            }
            const ll = lyr.getLatLng?.();
            if (!ll) return;
            const samePt = Math.abs(ll.lng - lon) < 1e-4 && Math.abs(ll.lat - lat) < 1e-4;
            if (!samePt) return;
            if (!matchName) {
                found = lyr;
                return;
            }
            const pname = String(lyr.feature?.properties?.h_name || '').trim();
            const pbrand = String(lyr.feature?.properties?.h_brand_name || '').trim();
            if (pname === matchName || pbrand === matchName || !pname) found = lyr;
        };
        for (const root of roots) walk(root);
        if (found?.openPopup) found.openPopup();
    }

    removePoi(category) {
        if (this.layers[category] && this.map) {
            this.map.removeLayer(this.layers[category]);
            delete this.layers[category];
            this.syncLegacyPoiLayers();
            this.setStatus('已清除');
        }
    }
}

customElements.define('poi-cabinet', PoiCabinet);

function dock() {
    if (window.appCore) {
        const instance = document.createElement('poi-cabinet');
        const container = document.getElementById('socket-poi');
        if (container) {
            container.innerHTML = '';
            container.appendChild(instance);
            window.appCore.registerCabinet('poi', instance);
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', dock);
} else {
    dock();
}
